import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RGPDCjueLink } from '../../types';

interface CJUELinksSectionProps {
  links: RGPDCjueLink[];
}

const CONFIDENCE_CONFIG = {
  high:   { labelKey: 'fiche.card.confidence.high',  color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  medium: { labelKey: 'fiche.card.confidence.medium',  color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  low:    { labelKey: 'fiche.card.confidence.low', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
} as const;

export const CJUELinksSection = ({ links }: CJUELinksSectionProps) => {
  const { t } = useTranslation();
  const validatedLinks = links.filter(l => l.validated === true);
  if (validatedLinks.length === 0) return null;

  const displayed = validatedLinks.slice(0, 3);
  const remaining = validatedLinks.length - displayed.length;

  return (
    <div className="px-5 pt-2.5 pb-0">
      <div className="flex items-center gap-1.5 mb-1.5">
        <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
        </svg>
        <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
          {t('fiche.card.cjue_title')}
        </span>
      </div>

      <div className="space-y-1.5">
        {displayed.map((link, i) => {
          const conf = link.confidence
            ? CONFIDENCE_CONFIG[link.confidence as keyof typeof CONFIDENCE_CONFIG]
            : null;
          return (
            <div key={i} className="flex flex-col gap-0.5">
              <div className="flex items-center flex-wrap gap-1.5">
                <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200">
                  {link.citation}
                </span>
                {link.celex && (
                  <span className="text-[9px] font-mono text-muted-foreground">
                    {link.celex}
                  </span>
                )}
                {conf && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${conf.color}`}>
                    {t(conf.labelKey)}
                  </span>
                )}
              </div>
              {link.explication && (
                <p className="text-[11px] text-muted-foreground italic leading-snug line-clamp-2">
                  {link.explication}
                </p>
              )}
            </div>
          );
        })}
        {remaining > 0 && (
          <p className="text-[10px] text-muted-foreground">
            {t('fiche.card.cjue_more', { count: remaining })}
          </p>
        )}
      </div>
    </div>
  );
};